import * as React from 'react'

// the persisted page state bags get stuck in session storage and then cleared when the user signs out or navigates to a new top level page.
const PAGE_STATE_PERSISTED_BAG_KEY = 'FieldOps::WebAdmin::PageStateBag'

interface PersistedPageStateBag {
  [key: string]: any
}

interface PersistedPageStateBags {
  [key: string]: PersistedPageStateBag
}

export interface PageState {
  title: string | null
  persistedState: PersistedPageStateBags
}

interface BasePersistedPageStatePayload {
  pagePathname: string
}

interface SetPersistedPageStateBagPayload
  extends BasePersistedPageStatePayload {
  state: any
}

type SetPersistedPageStateBagAction = {
  type: 'SetPersistedPageStateBag'
  payload: SetPersistedPageStateBagPayload
}

type SetCurrentTopLevelPageAction = {
  type: 'SetCurrentTopLevelPage'
  payload: BasePersistedPageStatePayload
}

type ClearAllPersistedPageStateBagsAction = {
  type: 'ClearAllPersistedPageStateBags'
}

type SetPageTitleAction = {
  type: 'SetPageTitle'
  payload: string
}

type ClearPageTitleAction = {
  type: 'ClearPageTitle'
}

type PageAction =
  | SetPageTitleAction
  | ClearPageTitleAction
  | SetPersistedPageStateBagAction
  | ClearAllPersistedPageStateBagsAction
  | SetCurrentTopLevelPageAction

export interface PageContextState {
  state: PageState
  dispatch: (action: PageAction) => void
}

const loadPersistedStateOrDefault = () => {
  const sessionStoragePersistedState = window.sessionStorage.getItem(
    PAGE_STATE_PERSISTED_BAG_KEY,
  )
  if (sessionStoragePersistedState === null) return {} as PersistedPageStateBags

  return JSON.parse(sessionStoragePersistedState) as PersistedPageStateBags
}

const DEFAULT_PAGE_STATUS: PageContextState = {
  state: {
    title: null,
    persistedState: loadPersistedStateOrDefault(),
  } as PageState,
  dispatch: (action: PageAction) => {},
}

const reducer = (state: PageState, action: PageAction) => {
  switch (action.type) {
    case 'SetPageTitle':
      return {
        ...state,
        title: action.payload,
      }
    case 'ClearPageTitle':
      return {
        ...state,
        title: null,
      }
    case 'SetPersistedPageStateBag':
      const newPersistedState = {
        ...state.persistedState,
        [action.payload.pagePathname]: action.payload.state,
      }
      // also persist this in SessionStorage
      window.sessionStorage.setItem(
        PAGE_STATE_PERSISTED_BAG_KEY,
        JSON.stringify(newPersistedState),
      )

      return {
        ...state,
        persistedState: newPersistedState,
      }

    case 'SetCurrentTopLevelPage':
      const pageDataToKeep = Object.keys(state.persistedState).filter(
        (pathname) => pathname === action.payload.pagePathname,
      )

      // clear all pages other than the new top level page
      const updatedPersistedState = {} as PersistedPageStateBags

      if (pageDataToKeep.length === 1) {
        updatedPersistedState[action.payload.pagePathname] =
          state.persistedState[action.payload.pagePathname]
      }

      // also persist this in SessionStorage
      window.sessionStorage.setItem(
        PAGE_STATE_PERSISTED_BAG_KEY,
        JSON.stringify(updatedPersistedState),
      )

      return {
        ...state,
        persistedState: updatedPersistedState,
      }
    case 'ClearAllPersistedPageStateBags': {
      window.sessionStorage.removeItem(PAGE_STATE_PERSISTED_BAG_KEY)

      return {
        ...state,
        persistedState: {},
      }
    }
    default:
      return state
  }
}

export const getInitialPageStatus = () => DEFAULT_PAGE_STATUS.state

export const PageContext = React.createContext<PageContextState>(
  DEFAULT_PAGE_STATUS,
)

type PageContextProviderProps = {
  children: any
}

export const PageContextProvider: React.FunctionComponent<PageContextProviderProps> = ({
  children,
}) => {
  const initialPageState = getInitialPageStatus()
  const [state, dispatch] = React.useReducer(reducer, initialPageState)
  const value = { state, dispatch }

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>
}

export const PageContextConsumer = PageContext.Consumer
